import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownSelectTrigger,
} from "@/lib/components/DropdownMenu";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/lib/components/Select";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import {
  ElementImageTimingDto,
  ElementTranstionDto,
  SceneTypeDto,
} from "@repo/api/overlays";
import { useMemo } from "react";
import Image from "@/lib/components/Image";
import ToggleGroup from "@/lib/components/ToggleGroup";
import { IconCheck } from "@tabler/icons-react";
import ElementTransitionSecondInput from "./ElementTransitionSecondInput";
import {
  CompositionTimings,
  findImageTiming,
} from "@repo/video-engine-i2v/utils/timing";
import { round } from "@/lib/utils";

type Props = {
  element: {
    externalId: string;
    enterAt: ElementTranstionDto;
    exitAt: ElementTranstionDto;
  };
  fieldName: "enterAt" | "exitAt";
  sceneType: SceneTypeDto;
  compositionTimings: CompositionTimings;
  onTimingChange: () => void;
};

export default function ElementTimingControls({
  element,
  fieldName,
  sceneType,
  compositionTimings,
  onTimingChange,
}: Props) {
  const primaryLabel = fieldName === "enterAt" ? "Enter at" : "Exit at";

  return (
    <div className="w-full">
      <div className="input-label">
        <span>{primaryLabel}</span>
        <span className="font-normal text-text-secondary"> (timing)</span>
      </div>
      <div className="flex flex-col gap-2">
        <TimingTypeSelect
          element={element}
          fieldName={fieldName}
          sceneType={sceneType}
          compositionTimings={compositionTimings}
          onTimingChange={onTimingChange}
        />
        {element[fieldName].timing?.type === "SECOND_TIMING" && (
          <SecondTimingControls
            element={element}
            fieldName={fieldName}
            sceneType={sceneType}
            compositionTimings={compositionTimings}
            onTimingChange={onTimingChange}
          />
        )}
        {element[fieldName].timing?.type === "IMAGE_TIMING" && (
          <ImageTimingControls
            element={element}
            fieldName={fieldName}
            sceneType={sceneType}
            compositionTimings={compositionTimings}
            onTimingChange={onTimingChange}
          />
        )}
      </div>
    </div>
  );
}

function TimingTypeSelect({
  element,
  fieldName,
  sceneType,
  onTimingChange,
}: Props) {
  const updateElement = useEditorStore((state) => state.service.updateElement);

  return (
    <Select
      value={element[fieldName].timing?.type ?? "NONE"}
      onValueChange={(value) => {
        updateElement(element.externalId, (element) => {
          if (value === "NONE") {
            element[fieldName].timing = null;
          } else if (value === "SECOND_TIMING") {
            element[fieldName].timing = {
              type: "SECOND_TIMING",
              seconds: 0,
            };
          } else if (value === "IMAGE_TIMING") {
            element[fieldName].timing = {
              type: "IMAGE_TIMING",
              imageIndex: 0,
              countFrom: "FIRST",
              seconds: 0,
            };
          }
        });

        onTimingChange();
      }}
    >
      <SelectTrigger
        className={`h-9 ${!element[fieldName].timing?.type ? "text-text-secondary" : ""}`}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="NONE">
          {fieldName === "enterAt" ? "Start" : "End"}
        </SelectItem>
        <SelectItem value="SECOND_TIMING">By second</SelectItem>
        {sceneType === "mainScene" && (
          <SelectItem value="IMAGE_TIMING">By image</SelectItem>
        )}
      </SelectContent>
    </Select>
  );
}

function SecondTimingControls({
  element,
  fieldName,
  sceneType,
  compositionTimings,
  onTimingChange,
}: Props) {
  const updateElement = useEditorStore((state) => state.service.updateElement);

  let maxSeconds = 10;
  const sceneTimings = compositionTimings[sceneType];

  if (sceneTimings) {
    maxSeconds = round(
      (sceneTimings.durationInFrames - 1) / compositionTimings.fps,
      1,
    );
  }

  return (
    <div className="w-full">
      <ElementTransitionSecondInput
        seconds={element[fieldName].timing!.seconds}
        min={0}
        max={maxSeconds}
        step={0.1}
        onCommitChange={(seconds) => {
          updateElement(element.externalId, (element) => {
            element[fieldName].timing!.seconds = seconds;
          });

          onTimingChange();
        }}
      />
    </div>
  );
}

function ImageTimingControls({
  element,
  fieldName,
  compositionTimings,
  onTimingChange,
}: Props) {
  const updateElement = useEditorStore((state) => state.service.updateElement);
  const timelineItems = useEditorStore(
    (state) => state.video?.compositionJson.mainSceneTimeline,
  );
  const sourceImages = useEditorStore((state) => state.sourceImages);

  const timing = element[fieldName].timing;

  const imageSrcs = useMemo(() => {
    if (!timelineItems || timing?.type !== "IMAGE_TIMING") {
      return [];
    }

    let srcs = timelineItems.map((timelineItem) => {
      const imageExternalId = timelineItem.startSourceImageExternalId;

      if (!imageExternalId) {
        return null;
      }

      if (!sourceImages[imageExternalId]) {
        throw new Error(`Source image not found for ${imageExternalId}`);
      }

      return sourceImages[imageExternalId]!.thumbnailSrc;
    });

    if (timing.countFrom === "LAST") {
      srcs = srcs.reverse();
    }

    return srcs;
  }, [sourceImages, timelineItems, timing]);

  if (timing?.type !== "IMAGE_TIMING") {
    return null;
  }

  let maxSeconds = 10;

  const imageTiming = findImageTiming(
    compositionTimings.mainScene.compositionImageTimings,
    timing,
  );

  if (imageTiming) {
    const framesLeft =
      compositionTimings.mainScene.globalEndAtFrame -
      imageTiming.globalStartAtFrame;
    maxSeconds = round(framesLeft / compositionTimings.fps, 1);
  }

  const imageIndex = timing.imageIndex;
  const countFromLast = timing.countFrom === "LAST";

  return (
    <div>
      <DropdownMenu>
        <DropdownSelectTrigger className="h-9">
          {`Image ${imageIndex + 1}${countFromLast ? " (from last)" : ""}`}
        </DropdownSelectTrigger>
        <DropdownMenuContent
          className="flex max-h-[600px] w-88 flex-col overflow-hidden p-0"
          align="center"
          side="right"
          collisionPadding={20}
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <div className="h-full w-full overflow-y-auto p-1">
            {imageSrcs.map((src, index) => (
              <DropdownMenuItem
                key={index}
                className="flex items-center [&_svg]:size-5"
                onClick={() => {
                  updateElement(element.externalId, (element) => {
                    if (element[fieldName].timing?.type !== "IMAGE_TIMING") {
                      throw new Error("Timing type is not IMAGE_TIMING");
                    }

                    element[fieldName].timing!.imageIndex = index;
                  });

                  onTimingChange();
                }}
              >
                <div className="h-[66px] w-[100px] overflow-hidden rounded-lg">
                  <Image
                    src={src ?? "/no-image-placeholder.jpg"}
                    className="h-full w-full object-cover object-center"
                  />
                </div>
                <p className="ml-3 flex-1 text-sm font-medium text-text-primary">
                  {`Image ${index + 1}`}
                </p>
                {index ===
                  (element[fieldName].timing as ElementImageTimingDto)
                    .imageIndex && (
                  <IconCheck size={16} className="mr-2 text-primary-400" />
                )}
              </DropdownMenuItem>
            ))}
          </div>
          <div className="h-px bg-border"></div>
          <div className="flex items-center px-4 py-2">
            <p className="flex-1 text-sm font-medium">Count from</p>
            <ToggleGroup
              options={[
                { label: "First image", value: "FIRST" },
                { label: "Last image", value: "LAST" },
              ]}
              value={
                (element[fieldName].timing as ElementImageTimingDto).countFrom
              }
              onChange={(value) => {
                updateElement(element.externalId, (element) => {
                  if (element[fieldName].timing?.type !== "IMAGE_TIMING") {
                    throw new Error("Timing type is not IMAGE_TIMING");
                  }

                  element[fieldName].timing!.countFrom = value as
                    | "FIRST"
                    | "LAST";
                });

                onTimingChange();
              }}
            />
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
      <div className="h-2"></div>
      <ElementTransitionSecondInput
        seconds={timing.seconds}
        min={0}
        max={maxSeconds}
        step={0.1}
        onCommitChange={(seconds) => {
          updateElement(element.externalId, (element) => {
            element[fieldName].timing!.seconds = seconds;
          });

          onTimingChange();
        }}
      />
    </div>
  );
}
